import { useTranslation } from "react-i18next";
import { useState } from "react";
import Api from "../../../Api";
import TeambookRadioButton from "../../default_components/TeambookRadioButton";
import { TeambookTeamsPicker, TeambookUsersPicker } from "../../default_components/TeambookDefaultPickers";
import lsKeys from "../../default_values/defaultKeys";
import TeambookDatePicker from "../../default_components/teambook_date_selector/DatePicker";
import FormErrorMessage from "../../default_components/FormErrorMessage";
import TeambookDialog from "../../default_components/TeambookDialog";
import PropTypes from "prop-types";

const BulkApproveWindow = ({ teams, users, date, setBulkApproveOpen, updateActuals }) => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(date.startOf("month"));
  const [endDate, setEndDate] = useState(date.endOf("month"));
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [approveType, setApproveType] = useState("users");
  const [errorMessage, setErrorMessage] = useState(null);

  const lang = localStorage.getItem(lsKeys.LANGUAGE);

  const closeWindow = () => {
    setBulkApproveOpen(false);
  };

  const bulkApprove = () => {
    if (startDate > endDate) {
      setErrorMessage(t("actuals.date_error"));
      return;
    }

    if (selectedUsers.length === 0 && selectedTeams.length === 0) {
      setErrorMessage(t("actuals.no_users_or_team_error"));
      return;
    }

    Api.ActualLogs.bulk_approve({
      from: startDate.toISODate(),
      to: endDate.toISODate(),
      userIds: selectedUsers,
      teamIds: selectedTeams,
    })
      .then(() => {
        updateActuals();
        closeWindow();
      })
      .catch((error) => {
        setErrorMessage(error.response.data.error.message);
      });
  };

  return (
    <TeambookDialog
      onClose={closeWindow}
      title={t("actuals.bulk_approve")}
      open={true}
      withCloseButton
      content={
        <>
          <div className="copy-planner-dialog__date-container">
            <div className="copy-planner-dialog__date-row">
              <p language={lang}>{`${t("projects.start_date")}`}</p>
              <p language={lang}>{`${t("projects.end_date")}`}</p>
            </div>

            <div className="copy-planner-dialog__date-row">
              <TeambookDatePicker
                onDayClick={(date) => {
                  setStartDate(date);
                  setEndDate(date.endOf("month"));
                }}
                from={startDate}
                range={1}
                type="default"
                variant="inline"
                formatStart={"d MMM, yyyy"}
                width={86}
                height={24}
                menuPosition={"bottom"}
              />
              <TeambookDatePicker
                onDayClick={(date) => setEndDate(date)}
                from={endDate}
                range={1}
                type="default"
                variant="inline"
                formatStart={"d MMM, yyyy"}
                width={86}
                height={24}
                menuPosition={"bottom"}
              />
            </div>
          </div>

          <div className="copy-planner-subblock">
            <p className="copy-planner-subblock__title">{t("planning.select_data")}</p>
            <div style={{ display: "flex" }}>
              <TeambookRadioButton
                checked={approveType === "users"}
                onCheck={() => {
                  setApproveType("users");
                  setSelectedTeams([]);
                }}
              />

              <TeambookUsersPicker
                type="multiple"
                users={users}
                placeholder={t("planning.select_users")}
                onChange={(options) => {
                  setApproveType("users");
                  setSelectedTeams([]);
                  setSelectedUsers(options.map((o) => o.id));
                }}
                value={selectedUsers.map((id) => ({
                  name: users.filter((u) => u.id === id)[0].name,
                  id: id,
                }))}
                maxMenuHeight={150}
                width={400}
              />
            </div>
            <div style={{ display: "flex" }}>
              <TeambookRadioButton
                checked={approveType === "teams"}
                onCheck={() => {
                  setApproveType("teams");
                  setSelectedUsers([]);
                }}
              />

              <TeambookTeamsPicker
                placeholder={t("planning.select_teams")}
                teams={teams}
                displayEmpty
                onChange={(options) => {
                  setApproveType("teams");
                  setSelectedUsers([]);
                  setSelectedTeams(options.map((o) => o.id));
                }}
                value={selectedTeams.map((id) => ({
                  name: teams.filter((t) => t.id === id)[0].name,
                  id: id,
                }))}
                type="multiple"
                maxMenuHeight={150}
                width={400}
              />
            </div>
          </div>

          {errorMessage && <FormErrorMessage text={errorMessage} closeErrorMessage={() => setErrorMessage(null)} />}
        </>
      }
      actionButtons={[
        {
          text: t("submit"),
          onClick: bulkApprove,
          id: "submitBulkApprove",
        },
      ]}
    />
  );
};

BulkApproveWindow.propTypes = {
  teams: PropTypes.array.isRequired,
  users: PropTypes.array.isRequired,
  date: PropTypes.object.isRequired,
  setBulkApproveOpen: PropTypes.func.isRequired,
  updateActuals: PropTypes.func.isRequired,
};

export default BulkApproveWindow;
